import { useSnackbar } from '@automata/rexports/notistack';
import {
  ErrorDisplay,
  Modal,
  TrackedButton,
  Typography,
  WarningCard,
} from '@automata/ui';
import { getErrorMessage } from '@automata/utils';
import { useFlag } from '@automata/utils/hooks';
import { useDeleteWorkflow } from '@automata/wfb-api';
import Box from '@mui/system/Box';
import Stack from '@mui/system/Stack';
import { useQueryClient } from '@tanstack/react-query';
import { useRouter } from 'next/router';
import { useCallback } from 'react';

import { setWorkflowIsDeleted } from '../../features/ui/uiSlice';
import { useAppDispatch } from '../../hooks';

interface Props {
  onClose: () => void;
  open: boolean;
  workflowId: string;
  workflowName: string;
}

export const DeleteWorkflowModal = ({
  workflowId,
  workflowName,
  open,
  onClose,
}: Props): JSX.Element | null => {
  const dispatch = useAppDispatch();
  const router = useRouter();
  const queryClient = useQueryClient();
  const apiBatchingEnabled = useFlag('fe.api_batching');
  const {
    mutateAsync: deleteWorkflow,
    isLoading: isDeleting,
    error,
  } = useDeleteWorkflow();

  const { enqueueSnackbar } = useSnackbar();

  const onSubmit = useCallback(async () => {
    try {
      await deleteWorkflow({
        pathParams: { workflowId: workflowId },
      });
      enqueueSnackbar('Workflow deleted', { variant: 'success' });
      onClose();
      dispatch(setWorkflowIsDeleted(true));
      queryClient.invalidateQueries(['v3', 'workflow']);
      router.push(`/${router.query.org}/canvas-workflows`);
    } catch (err: unknown) {
      const error = getErrorMessage(err, 'unknown error');
      enqueueSnackbar(`Cannot delete workflow: ${error}`, {
        variant: 'error',
      });
    }
  }, [
    deleteWorkflow,
    dispatch,
    enqueueSnackbar,
    onClose,
    queryClient,
    router,
    workflowId,
  ]);

  return (
    <Modal onClose={onClose} open={open} title={`Delete "${workflowName}"`}>
      <Box paddingY={1}>
        <Typography>Are you sure you want to continue?</Typography>
        {apiBatchingEnabled && (
          <Box paddingY={2}>
            <WarningCard message="Deleting this workflow here will also permanently remove the published workflow on the workflows page. This action cannot be undone." />
          </Box>
        )}
        {error !== null && (
          <ErrorDisplay error={error} userMessage="Failed to delete workflow" />
        )}
        <Stack direction="row" justifyContent="flex-end" spacing={2}>
          <TrackedButton
            trackLabel="screen.visualWorkflow.deleteCancel"
            disabled={isDeleting}
            color="secondary"
            variant="text"
            onClick={onClose}
          >
            Cancel
          </TrackedButton>
          <TrackedButton
            trackLabel="screen.visualWorkflow.delete"
            loading={isDeleting}
            variant="contained"
            type="submit"
            onClick={onSubmit}
          >
            Delete
          </TrackedButton>
        </Stack>
      </Box>
    </Modal>
  );
};
